import { HANDLED, SKIP } from './constants'

/**
 * Page-level error handler — suppresses the default ErrorModal.
 *
 * Use this handler on `apiAction` calls where the page already renders its
 * own inline error state (e.g., an <Alert> with a Retry button, or a
 * full-page error illustration) driven by the `failureActionType` → reducer
 * → selector → component pipeline.
 *
 * The `failureActionType` is still dispatched and the reducer still sets
 * its error flag — the user sees the inline error UI as intended, without
 * the blocking ErrorModal on top of it.
 *
 * This handler matches ALL errors unconditionally and should be the only
 * entry in `errorHandlers` for these calls. No domain-specific handlers
 * are needed before it, because the page's own rendering handles the UX.
 *
 * Usage:
 *   import { pageErrorHandler } from 'sagas/errorHandlers'
 *
 *   apiAction({
 *     ...,
 *     failureActionType: MY_PAGE_FAILURE,  // reducer sets error state → component renders error UI
 *     errorHandlers: [pageErrorHandler],   // suppress the default modal overlay
 *   })
 *
 * **Trade-off — navigation-away requests:** If the user navigates away after
 * the API call is dispatched but before the response arrives, the page's inline
 * error component may have already unmounted. In that case the error will still
 * be suppressed (HANDLED is returned), the failureActionType will still be
 * dispatched, and `console.error` will log it. There is no visible feedback to
 * the user — but this is an accepted trade-off for page-owned error flows: the
 * user has already left the page, so showing a blocking modal would be
 * disorienting.
 *
 * @returns {'handled'}
 */
export default function* pageErrorHandler(err) {
  // Auth errors (401 Unauthorized / 403 Forbidden) must NOT be suppressed by
  // this handler. Return SKIP so they continue through the chain.
  //
  // NOTE: in practice, fetchFailureHandler (system tier, always runs first)
  // already catches 401/403 and redirects to SiteMinder before any user
  // handler runs. This SKIP is belt-and-suspenders in case that system handler
  // changes, and also documents intent: this handler must not suppress auth
  // errors even if the system tier doesn't handle them first.
  if (err.status === 401 || err.status === 403) {
    return SKIP
  }

  // Log to console only in development so failures remain visible in devtools
  // without leaking the full error object (which may include internal API paths
  // via err.requestUri) to production telemetry/error-reporting integrations.
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.error(
      '[pageErrorHandler] Error suppressed — inline error UI expected:',
      err
    )
  }

  // No saga effects needed — this handler's sole purpose is to return HANDLED
  // so the defaultErrorHandler at the end of the chain never runs.
  // The yield* expression delegates to an empty generator to satisfy the
  // require-yield ESLint rule while keeping this a valid generator function.
  yield* []
  return HANDLED
}
